import { addDays, isoDay } from 'helper/timeLabels';

/**
 * What the CRM api answers, drawn from its database (crmMockData.js): the
 * home page of a user, their tasks or their colleagues', a task. Pure: the
 * tests read them on a fixed day (`now`).
 */

// What a demo appointment is called in a list of done things.
const CATEGORY_TITLES = {
  prospection: 'Visite terrain',
  visite_client: 'Visite client',
  rdv_client: 'Rendez-vous client',
  embauche: 'Visite embauche',
  presentation: 'Présentation de Jobbiz',
  telephone: 'Rendez-vous téléphonique',
};
export const companyOf = (db, id) => db.companies.find((item) => item.id === id) ?? null;
export const personOf = (db, id) => db.team.find((member) => member.id === id) ?? null;
export const taskOf = (db, id) => db.tasks.find((item) => item.id === id);
const sameDay = (iso, day) => Boolean(iso) && isoDay(new Date(iso)) === day;
const endOfDay = (now) => {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return end;
};

export const withCompany = (db, item, fields) => {
  const found = companyOf(db, item.companyId);
  if (!found) return { ...item, company: null };
  return { ...item, company: Object.fromEntries(fields.map((field) => [field, found[field]])) };
};

export const homeView = (db, me, now = new Date()) => {
  const today = isoDay(now);
  const end = endOfDay(now);
  return {
    appointments: db.appointments
      .filter((item) => sameDay(item.startsAt, today))
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
      .map((item) => withCompany(db, item, ['id', 'name', 'city', 'contactName'])),
    tasks: db.tasks
      .filter((item) => item.assigneeId === me.id)
      .filter((item) => (item.doneAt ? sameDay(item.doneAt, today) : item.dueOn <= today))
      .map((item) => withCompany(db, item, ['id', 'name', 'contactName'])),
    calls: db.prospects
      ? {
          made: db.calls.made,
          pending: db.callbacks
            .filter((item) => !item.doneAt && new Date(item.dueAt) < end)
            .map((item) => withCompany(db, item, ['id', 'name', 'contactName'])),
          toQualify: db.calls.toQualify,
          appointmentsBooked: db.calls.appointmentsBooked,
        }
      : null,
    // The visits to place in the planning, those due by tomorrow apart.
    appointmentsToPlace: {
      total: db.visits.length,
      beforeTomorrow: db.visits.filter((item) => item.dueBy <= isoDay(addDays(now, 1))).length,
    },
  };
};

export const tasksView = (db, me, people) => {
  const ids = people === 'team' ? null : people?.length ? people : [me.id];
  const mine = !ids || ids.includes(me.id);
  return {
    team: db.team,
    tasks: db.tasks
      .filter((item) => !ids || ids.includes(item.assigneeId))
      .map((item) => ({ ...withCompany(db, item, ['id', 'name']), unreadComments: item.unread })),
    doneAgenda: !mine
      ? []
      : [
          ...db.appointments
            .filter((item) => item.status === 'done')
            .map((item) => ({
              id: item.id,
              kind: 'appointment',
              title: CATEGORY_TITLES[item.category] ?? item.title,
              dueOn: isoDay(new Date(item.startsAt)),
              doneAt: item.startsAt,
              contactName: item.contactName,
              company: withCompany(db, item, ['id', 'name']).company,
            })),
          ...db.callbacks
            .filter((item) => item.doneAt && item.kind !== 'new')
            .map((item) => {
              const found = companyOf(db, item.companyId);
              return {
                id: item.id,
                kind: 'callback',
                title: `Rappeler ${found?.contactName || found?.name || ''}`.trim(),
                dueOn: isoDay(new Date(item.dueAt)),
                doneAt: item.doneAt,
                contactName: found?.contactName ?? '',
                company: found ? { id: found.id, name: found.name } : null,
              };
            }),
        ],
  };
};

export const taskView = (db, item) => ({
  ...withCompany(db, item, ['id', 'name', 'city', 'contactName', 'contactRole', 'phone', 'email', 'stage']),
  createdByPerson: personOf(db, item.createdBy),
  unreadComments: item.unread,
});

